import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiErrorDetail } from "@/lib/api";
import { Building2, ShieldCheck, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Connecté");
      navigate(from, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[hsl(var(--primary))] text-white p-12 relative overflow-hidden">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-md bg-white flex items-center justify-center p-1">
              <LogoMark className="h-7" />
            </span>
            <div className="font-heading font-bold text-lg tracking-tight">Gestimmony</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Gérez votre parc locatif en un clin d'œil.
          </h1>
          <p className="mt-4 text-white/80 text-base leading-relaxed">
            Biens, locataires, baux, quittances — automatisez la paperasse et gardez le contrôle
            sur vos loyers, impayés et échéances.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: Building2, text: "Suivi centralisé des biens et locataires" },
              { icon: BarChart3, text: "Tableau de bord et rentabilité en temps réel" },
              { icon: ShieldCheck, text: "Génération PDF de baux et quittances conforme" },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/90">
                <div className="h-8 w-8 rounded-md bg-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-white/60">© 2026 Gestimmony</div>

        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* Right panel — form */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <LogoMark className="h-9" />
            <div className="font-heading font-bold text-lg">Gestimmony</div>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Connexion
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Accédez à votre espace propriétaire / gestionnaire.
          </p>

          <Card className="mt-6 p-6 border-slate-200 shadow-none">
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-md"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
              >
                {busy ? "Connexion…" : "Se connecter"}
              </Button>

              <div className="text-center pt-1">
                <Link
                  to="/forgot-password"
                  data-testid="link-forgot-password"
                  className="text-xs text-slate-500 hover:text-[hsl(var(--primary))] hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>
          </Card>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Pas encore de compte ?{" "}
            <Link to="/register" data-testid="link-register" className="text-[hsl(var(--primary))] font-medium hover:underline">
              Créer un compte gestionnaire
            </Link>
          </p>

          <div className="mt-6 flex items-center text-xs text-slate-500">
            <Link to="/" data-testid="login-back-home" className="hover:text-[hsl(var(--primary))]">← Gestimmony</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
